import { throttle } from 'lodash'
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { TransitionGroup, CSSTransition } from 'react-transition-group'

import { BoxBase } from 'styles/components/layout'

import { Number, Square, Inner } from './styles'

const FORWARD = 'FORWARD'
const REVERSE = 'REVERSE'
let ScrollMagic

/**
 * StickySquare component
 *
 * @param {props} props - properties
 * @return {Component} Component - StickySquare
 */
class StickySquare extends Component {
  constructor(props) {
    super(props)

    this.innerSquares = props.blocks.map(() => React.createRef())
    this.squareRef = React.createRef()
    this.controller = null
    this.mainScene = null
    this.scenes = null
    this.throttledUpdateScene = throttle(this.updateScene, 100)
    this.state = {
      squareContent: 1,
      squareContentColor: '',
      isAppeared: false,
      blocksHeight: 0,
    }
  }

  componentDidMount() {
    const { topOffsetNode } = this.props

    ScrollMagic = require('ScrollMagic')

    this.controller = new ScrollMagic.Controller()
    window.addEventListener('resize', this.throttledUpdateScene)
    window.addEventListener('beforeunload', this.moduleScrollTop)
    this.createMainScene()

    if (topOffsetNode) {
      this.mainScene.offset(-topOffsetNode.getBoundingClientRect().height * 2)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { isActive, topOffsetNode, blocks } = this.props
    const { isAppeared } = this.state
    const { top: lastTop } = blocks[
      blocks.length - 1
    ].current.getBoundingClientRect()
    const { top: firstTop } = blocks[0].current.getBoundingClientRect()
    const duration = lastTop - firstTop

    if (this.mainScene && duration !== prevState.blocksHeight) {
      this.checkBlocksHeight()
    }

    if (!isAppeared && isActive !== prevProps.isActive) {
      this.updateSceneWhenAppear()
    }

    if (topOffsetNode !== prevProps.topOffsetNode) {
      this.mainScene.offset(-topOffsetNode.getBoundingClientRect().height * 2)
    }
  }

  componentWillUnmount() {
    const { deactivateStickyHandler } = this.props

    window.removeEventListener('resize', this.throttledUpdateScene)
    window.removeEventListener('beforeunload', this.moduleScrollTop)
    if (this.controller) {
      this.controller.destroy()
      this.controller = null
      this.mainScene = null
      this.scenes = null
      deactivateStickyHandler()
    }
  }

  moduleScrollTop = () => window.scrollTo(0, 0)

  checkBlocksHeight = () => {
    const { blocks } = this.props
    const { blocksHeight } = this.state
    const { top: lastTop } = blocks[
      blocks.length - 1
    ].current.getBoundingClientRect()
    const { top: firstTop } = blocks[0].current.getBoundingClientRect()
    const duration = lastTop - firstTop

    if (blocksHeight === duration) {
      this.updateScene()
    } else {
      this.setState({ blocksHeight: duration })
    }
  }

  createMainScene = () => {
    const {
      blocks,
      activateStickyHandler,
      deactivateStickyHandler,
    } = this.props

    const { top: lastTop } = blocks[
      blocks.length - 1
    ].current.getBoundingClientRect()
    const { top: firstTop } = blocks[0].current.getBoundingClientRect()
    const duration = lastTop - firstTop

    this.mainScene = new ScrollMagic.Scene({
      triggerElement: blocks[0].current,
      triggerHook: 0,
      duration,
    })
      .on('enter', ev => {
        activateStickyHandler()
        if (!this.scenes) {
          this.createScenes()
        }
        if (ev.scrollDirection === FORWARD) {
          this.innerSquares[0].current.style.transform = 'translateY(0)'
        } else {
          this.innerSquares[
            this.innerSquares.length - 1
          ].current.style.transform = 'translateY(0)'
        }
      })
      .on('leave', deactivateStickyHandler)
      .addTo(this.controller)
  }

  createScenes = () => {
    const { blocks, textColors } = this.props
    const { height, top } = this.squareRef.current?.getBoundingClientRect()

    this.scenes = blocks.map((item, index) =>
      new ScrollMagic.Scene({
        triggerElement: item.current,
        triggerHook: 0,
        offset: -height - top,
        duration: height,
      })
        .on('enter', () => {
          if (
            this.innerSquares[index - 1] &&
            this.innerSquares[index - 1].current
          ) {
            this.innerSquares[index - 1].current.style.transform =
              'translateY(0)'
          }
        })
        .on('progress', ev => {
          const square = this.innerSquares[index]
          const prevSquare = this.innerSquares[index - 1]

          if (
            ev.scrollDirection === REVERSE &&
            prevSquare &&
            prevSquare.current &&
            !prevSquare.current.hasAttribute('style') &&
            ev.progress <= 0.1
          ) {
            this.innerSquares[index - 1].current.style.transform =
              'translateY(0)'
          }

          if (square && square.current) {
            square.current.style.transform = `translateY(${100 -
              100 * ev.progress}%)`
          }

          if (ev.progress > 0.52) {
            this.setState({
              squareContent: index + 1,
              squareContentColor: textColors[index],
            })
          } else if (ev.progress < 0.48) {
            this.setState({
              squareContent: index,
              squareContentColor: textColors[index - 1],
            })
          }
        }),
    )

    this.controller.addScene(this.scenes)
  }

  updateScene = () => {
    const { blocks } = this.props
    const { top: lastTop } = blocks[
      blocks.length - 1
    ].current.getBoundingClientRect()
    const { top: firstTop } = blocks[0].current.getBoundingClientRect()
    const duration = lastTop - firstTop

    this.mainScene && this.mainScene.duration(duration)
    if (
      this.scenes &&
      this.squareRef.current &&
      'getBoundingClientRect' in this.squareRef.current
    ) {
      const { height, top } = this.squareRef.current.getBoundingClientRect()

      this.scenes.forEach(scene => {
        scene.duration(height)
        scene.offset(-height - top)
      })
    }
  }

  updateSceneWhenAppear = () =>
    this.setState({ isAppeared: true }, this.updateScene)

  render() {
    const { blocks, squareColors, isActive } = this.props
    const { squareContent, squareContentColor } = this.state

    return (
      isActive && (
        <BoxBase>
          <Square ref={this.squareRef}>
            {blocks.map((item, index) => (
              <Inner
                ref={this.innerSquares[index]}
                key={`item.${index}`}
                bgColor={squareColors[index]}
              />
            ))}
            <TransitionGroup component={null}>
              <CSSTransition
                key={`key-${squareContent}`}
                timeout={150}
                classNames="sticky-square__content-"
              >
                {status => (
                  <Number
                    as="div"
                    state={status}
                    color={squareContentColor}
                    dangerouslySetInnerHTML={{ __html: squareContent }}
                  />
                )}
              </CSSTransition>
            </TransitionGroup>
          </Square>
        </BoxBase>
      )
    )
  }
}

StickySquare.propTypes = {
  /** Function that activate sticky square */
  activateStickyHandler: PropTypes.func.isRequired,

  /** Sections reference */
  blocks: PropTypes.array.isRequired,

  /** Function that deactivate sticky square */
  deactivateStickyHandler: PropTypes.func.isRequired,

  /** Flag to control component's state */
  isActive: PropTypes.bool.isRequired,

  /** Background colors array */
  squareColors: PropTypes.array.isRequired,

  /** Text colors array */
  textColors: PropTypes.array.isRequired,

  /** Node reference for offset */
  topOffsetNode: PropTypes.object,
}

export default StickySquare
